import {
  Box,
  BoxProps,
  Flex,
  Text,
  Tooltip,
  useRadio,
  useRadioGroup,
  UseRadioProps,
} from "@chakra-ui/react";

type SegmentedControlOption<T extends string> = {
  label: string;
  value: T;
  disabled?: boolean;
  disabledReason?: string;
};

type SegmentedControlProps<T extends string> = {
  options: SegmentedControlOption<T>[];
  value: T;
  onChange: (val: T) => void;
  radioProps?: BoxProps;
};

// This component mimics the Chakra UI SegmentGroup component, while we are still on Chakra v2.
// SegmentGroup is new in Chakra v3.
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  radioProps: additionalRadioProps,
}: SegmentedControlProps<T>) {
  const { getRootProps, getRadioProps } = useRadioGroup({
    name: "segmented",
    value,
    onChange: (nextValue: string) => {
      const selected = options.find((option) => option.value === nextValue);
      if (selected) {
        onChange(selected.value);
      }
    },
  });
  const group = getRootProps();

  return (
    <Flex
      position="relative"
      {...group}
      width="100%"
      bg="gray.100"
      borderRadius="md"
    >
      {options.map((option) => {
        const radioProps = getRadioProps({
          value: option.value,
          isDisabled: option.disabled,
        });
        const radio = (
          <CustomRadio containerProps={additionalRadioProps} {...radioProps}>
            {option.label}
          </CustomRadio>
        );
        return option.disabled && option.disabledReason ? (
          <Tooltip
            label={<Text align="center">{option.disabledReason}</Text>}
            hasArrow
            placement="top"
            borderRadius="md"
            key={option.value}
          >
            <Box flex={1}>{radio}</Box>
          </Tooltip>
        ) : (
          <Box key={option.value} flex={1}>
            {radio}
          </Box>
        );
      })}
    </Flex>
  );
}

const CustomRadio = (
  props: UseRadioProps & {
    children: React.ReactNode;
    containerProps?: BoxProps;
  }
) => {
  const { getInputProps, getRadioProps, state } = useRadio(props);
  const inputProps = getInputProps();
  const radioProps = getRadioProps();

  return (
    <Box as="label">
      <input {...inputProps} />
      <Box
        {...radioProps}
        textAlign="center"
        borderRadius="md"
        fontWeight="medium"
        fontSize="sm"
        bg={state.isChecked ? "white" : "gray.100"}
        boxShadow={state.isChecked ? "md" : "none"}
        border="1px solid"
        borderColor={state.isChecked ? "gray.300" : "transparent"}
        _hover={{
          bg: state.isChecked
            ? "white"
            : state.isDisabled
            ? "gray.100"
            : "gray.200",
        }}
        p={1}
        cursor={state.isDisabled ? "not-allowed" : "pointer"}
        {...props.containerProps}
      >
        {props.children}
      </Box>
    </Box>
  );
};
